import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faBell, faBellSlash } from '@fortawesome/free-solid-svg-icons';
import notificationService from '../services/notificationService';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, isPushSubscribed } from '../services/pushService';
import UserAvatar from '../components/UserAvatar';
import './MobileNotificationsPage.css';

const MobileNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported] = useState(isPushSupported());

  // Fewer items per page for mobile
  const limit = 10;

  useEffect(() => {
    loadNotifications();
  }, [currentPage]);

  useEffect(() => {
    if (pushSupported) {
      isPushSubscribed().then(setPushEnabled).catch(() => {});
    }
  }, [pushSupported]);

  // Function to update the global notification count for the navbar
  const updateGlobalNotificationCount = (count) => {
    // This will dispatch an event that the MobileNavBar component can listen for
    const event = new CustomEvent('notificationCountUpdated', { 
      detail: { unreadCount: count } 
    });
    document.dispatchEvent(event);
  };

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const offset = (currentPage - 1) * limit;
      
      const response = await notificationService.fetchNotifications({
        limit,
        offset,
        unreadOnly: false // Always fetch all notifications
      });
      
      setNotifications(response.notifications || []);
      setTotalCount(response.total_count || 0);
      setUnreadCount(response.unread_count || 0);
      
      // Update global notification count
      updateGlobalNotificationCount(response.unread_count || 0);
    } catch (error) {
      setError('Failed to load notifications. Please try again later.');
      // Set empty data in case of error
      setNotifications([]);
      setTotalCount(0);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );
      
      const newUnreadCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newUnreadCount);
      
      // Update global notification count
      updateGlobalNotificationCount(newUnreadCount);
    } catch (error) {
      setError('Failed to mark notification as read. Please try again.');
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'pravkar';
    if (diffMin < 60) return `${diffMin}m nazaj`;
    if (diffHour < 24) return `${diffHour}h nazaj`;
    if (diffDay < 7) return `${diffDay}d nazaj`;
    return date.toLocaleDateString();
  };

  // Get appropriate link based on notification type
  const getNotificationLink = (notification) => {
    if (notification.reference_type === 'event' && notification.reference_id) {
      return `/dashboard?event=${notification.reference_id}`;
    } else if (notification.reference_type === 'community' && notification.reference_id) {
      return `/community/${notification.reference_id}`;
    }
    return '#';
  };

  const handleTogglePush = async () => {
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
      } else {
        const success = await subscribeToPush();
        setPushEnabled(success);
      }
    } catch (error) {
      // Permission denied or other error
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="updated-event-view">
      <div className="event-scrollable-content">
        {/* Header section - Updated to match MobileEventsList */}
        <div className="event-header-container">
          <div className="event-header-content">
            <div className="event-info d-flex justify-content-between align-items-center w-100">
              <h2 className="event-title mb-0">Obvestila</h2>
              {pushSupported && (
                <button
                  className={`btn btn-sm ${pushEnabled ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                  onClick={handleTogglePush}
                  style={{ fontSize: '0.8rem' }}
                >
                  <FontAwesomeIcon icon={pushEnabled ? faBell : faBellSlash} className="me-1" />
                  {pushEnabled ? 'Push ON' : 'Push OFF'}
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Main content area */}
        <div className="event-content">
          <div className="event-section">
            {error && (
              <div className="notification-error">
                {error}
              </div>
            )}
            
            {loading ? (
              <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : notifications.length > 0 ? (
              <div className="mobile-notifications-list">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : 'read'}`}
                  >
                    <Link
                      to={getNotificationLink(notification)}
                      className="notification-link"
                      onClick={() => {
                        if (!notification.is_read) {
                          handleMarkAsRead(notification.id);
                        }
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div className="notification-avatar me-3">
                          {notification.actor ? (
                            <UserAvatar
                              profileImage={notification.actor.profile_image}
                              displayName={notification.actor.display_name}
                              email={notification.actor.email}
                              size={40}
                              avatarOnly={true}
                            />
                          ) : (
                            <div
                              className="rounded-circle d-flex justify-content-center align-items-center"
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#6c757d',
                                color: 'white'
                              }}
                            >
                              <FontAwesomeIcon icon={faBell} />
                            </div>
                          )}
                        </div>
                        <div className="notification-text-wrapper">
                          <div className="notification-content">
                            {notification.content}
                          </div>
                          <div className="notification-meta">
                            <span className="notification-time">{formatTimeAgo(notification.created_at)}</span>
                            <span className="notification-type">{notification.type ? notification.type.replace(/_/g, ' ') : 'obvestilo'}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-content">
                <p className="empty-program-text">Ni obvestil</p>
              </div>
            )}
            
            {/* Mobile-friendly pagination */}
            {totalPages > 1 && (
              <div className="mobile-pagination">
                <button 
                  className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Prejšnja
                </button>
                
                <span className="page-count">
                  {currentPage} / {totalPages}
                </span>
                
                <button 
                  className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Naslednja <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileNotificationsPage;